import { Middleware } from "@reduxjs/toolkit";
import { setUser, logout } from "../slices/authSlice";
import { authService } from "../../services/authService";
import { STORAGE_KEYS, COOKIE_KEYS } from "../../constants";
import { setCookie, deleteCookie } from "../../utils/cookieUtils";

let isUserDataLoading = false;

const persistToken = (token: {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  issued_at?: number;
}) => {
  const { refresh_token, ...accessTokenData } = token;
  setCookie(COOKIE_KEYS.ACCESS_TOKEN, JSON.stringify(accessTokenData), 1);
  setCookie(COOKIE_KEYS.REFRESH_TOKEN, refresh_token, 7);
};

export const authMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action);

  // Handle storage operations based on auth actions
  switch (action.type) {
    case "auth/loginStart":
      // Clear registration token when starting login
      localStorage.removeItem(STORAGE_KEYS.REGISTRATION_TOKEN);
      break;

    case "auth/loginSuccess":
    case "auth/twoFactorSuccess":
      if (action.payload?.token) {
        persistToken(action.payload.token);
        // Token refresh scheduling is handled by useTokenManager hook
      }
      break;

    case "auth/logout":
      deleteCookie(COOKIE_KEYS.ACCESS_TOKEN);
      deleteCookie(COOKIE_KEYS.REFRESH_TOKEN);
      deleteCookie(COOKIE_KEYS.EMAIL_HINT);
      deleteCookie(COOKIE_KEYS.PHONE_HINT);
      deleteCookie(COOKIE_KEYS.FORGOT_EMAIL_HINT);
      localStorage.removeItem(STORAGE_KEYS.REGISTRATION_TOKEN);
      sessionStorage.clear();
      break;

    case "auth/setRegistrationToken":
      // Save registration token to localStorage
      if (action.payload) {
        localStorage.setItem(STORAGE_KEYS.REGISTRATION_TOKEN, action.payload);
      }
      break;

    case "auth/tokenRefreshed":
      if (action.payload?.token) {
        persistToken(action.payload.token);
      }
      break;
  }

  // Check if user is authenticated but user data is not loaded (only for initializeAuth)
  if (action.type === "auth/initializeAuth") {
    const state = store.getState();
    const { isAuthenticated, user, token } = state.auth;

    if (isAuthenticated && token && !user && !isUserDataLoading) {
      isUserDataLoading = true;
      authService
        .getCurrentUser()
        .then((userData) => {
          store.dispatch(setUser(userData));
          console.log("User data loaded, no token validation needed");
        })
        .catch((_error) => {
          const isRegistrationFlow = localStorage.getItem(
            STORAGE_KEYS.REGISTRATION_TOKEN,
          );
          if (!isRegistrationFlow) {
            store.dispatch(logout());
          }
        })
        .finally(() => {
          isUserDataLoading = false;
        });
    } else if (isAuthenticated && token && user) {
      console.log("User authenticated, no token validation needed");
    }
  }

  return result;
};
